"use client";

import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Box,
  SlidersHorizontal,
  PencilLine,
  Users,
  Trash2,
  ChevronLeft,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { rotation3dWhiteIcon } from "@/lib/Images";
import ExperienceUsersListDialog from "@/components/dialogs/tenants/experiences/ExperienceUsersListDialog";
import { useGetExperienceByCampaignQuery } from "@/store/slices/apiSlice";
import { formatDate, formatDuration } from "@/lib/utils";
import CircleLoader from "@/components/common/ActionLoader";
import AddExperienceDialog from "@/components/dialogs/tenants/experiences/AddExperienceDialog";

const dummyExperiences = [
  {
    id: "experience-001",
    title: "Fire Safety Simulation",
    createdAt: "Jan 15, 2026",
    description:
      "Learn emergency evacuation procedures and fire extinguisher usage in realistic scenarios.",
    status: "In Drafts",
    difficulty: "Medium",
    duration: "15 min",
    cardColor: "#00552D",
    disabledConfigurator: false,
  },
  {
    id: "experience-002",
    title: "Hazard Identification Simulation",
    createdAt: "Jan 15, 2026",
    description:
      "Identify and respond to workplace hazards before they become incidents.",
    status: "Published",
    difficulty: "Medium",
    duration: "15 min",
    cardColor: "#6B1F0F",
    disabledConfigurator: true,
  },
  {
    id: "experience-003",
    title: "Heavy Equipment Operation Simulation",
    createdAt: "Jan 15, 2026",
    description:
      "Master the controls and safety procedures for industrial machinery and tools.",
    status: "In Drafts",
    difficulty: "Medium",
    duration: "15 min",
    cardColor: "#063E63",
    disabledConfigurator: false,
  },
];

const ExperienceListing = () => {
  const navigate = useNavigate();

  const location = useLocation();
  const { campaignId: campaignIdFromParams } = useParams();

  const campaignId = location.state?.campaignId || campaignIdFromParams;
  const tenantId = location.state?.tenantId || campaignIdFromParams;

  useEffect(() => {
    if (!campaignId) {
      navigate("/tenants/profile", { replace: true });
    }
  }, [campaignId, navigate]);

  const { campaignName, tenantName, deepLink } = location?.state || {};
  // console.log("CampaignId ", campaignName);
  const { data: experienceListByCampaign, isLoading: isExperinceLoading } =
    useGetExperienceByCampaignQuery(
      { campaignId },
      {
        skip: !campaignId,
      },
    );

  // const experienceDetails = experienceList?.response?.data;
  const [openAddExperience, setOpenAddExperience] = useState(false);
  const [experiences, setExperiences] = useState([]);
  const [openUsersDialog, setOpenUsersDialog] = useState(false);
  const [selectedExperience, setSelectedExperience] = useState(null);
  const [openCreateExperience, setOpenCreateExperience] = useState(false);
  const handleRemoveExperience = (experienceId) => {
    setExperiences((prev) =>
      prev.filter((experience) => experience.id !== experienceId),
    );
  };

  const tenantId1 =
    location.state?.tenantId ||
    experienceListByCampaign?.response?.data?.[0]?.tenantId;

  useEffect(() => {
    const experienceDetails = experienceListByCampaign?.response?.data;

    if (Array.isArray(experienceDetails)) {
      const publishedExperiences = experienceDetails.filter(
        (experience) => experience.status === "PUBLISHED",
      );

      setExperiences(publishedExperiences);
    } else {
      setExperiences([]);
    }
  }, [experienceListByCampaign]);

  return (
    <div className="min-h-screen  px-6 py-6 text-neutral-100">
      <Button
        type="button"
        variant="neutral"
        onClick={() =>
          navigate(
            "/tenants/profile",
            {
              state: {
                tenantId: tenantId || tenantId1,
              },
            },
            { replace: true },
          )
        }
        className="mb-7 rounded-md border border-brand-20  text-xs font-semibold text-[#7EDBFF] hover:bg-white/5"
      >
        <ChevronLeft className="h-4 w-4" />
        Back
      </Button>
      <CircleLoader loading={isExperinceLoading} size={64} />
      <div className="mb-5 flex items-center justify-between">
        <h1 className="mb-6 text-xl font-bold text-neutral-100">
          All Experiences
        </h1>
        <Button
          type="button"
          onClick={() => setOpenAddExperience(true)}
          className="h-9 rounded-md bg-brand-21 px-5 text-sm font-bold text-neutral-100 hover:bg-brand-22"
        >
          + Create Experience
        </Button>
      </div>
      <div className="space-y-5">
        {experiences?.length > 0
          ? experiences.map((experience) => (
              <ExperienceCard
                key={experience.id}
                experience={experience}
                onOpenConfigurator={() =>
                  navigate(`/tenants/campaigns/experiences/edit`, {
                    state: {
                      defaultCampaign: campaignName,
                      tenantName,
                      deepLink,
                      experienceId: experience._id || experience.id,
                      templateId: experience.simulationTemplateId,
                      campaignId: experience.campaignId,
                      simulationTemplateId: experience.simulationTemplateId,
                      estimate_time: experience.estimate_time,
                    },
                  })
                }
                onEditDetails={() =>
                  console.log("Edit experience details:", experience)
                }
                onSeeUsers={() => {
                  // navigate(`/experiences/${experience._id}/users`);
                  setSelectedExperience(experience);
                  setOpenUsersDialog(true);
                }}
                onRemove={() => handleRemoveExperience(experience.id)}
              />
            ))
          : !isExperinceLoading && (
              <div className="flex min-h-[420px] flex-col items-center justify-center text-center">
                <div className="mb-6 flex h-24 w-24 items-center justify-center rounded-3xl bg-gradient-to-br from-[#103B73] to-[#2D235B]">
                  <img
                    src={rotation3dWhiteIcon}
                    alt="experience"
                    className="h-16 w-16 object-contain"
                  />
                </div>

                <h2 className="mb-3 text-3xl font-medium text-neutral-100">
                  No Experiences Available Yet
                </h2>

                <p className="mb-8 max-w-[620px] text-sm font-medium text-neutral-100/60">
                  Create a new experience from a template and publish it to make
                  it available to users.
                </p>

                <Button
                  type="button"
                  onClick={() => setOpenAddExperience(true)}
                  className="h-11 rounded-md bg-gradient-to-r from-[#188CEB] to-[#3758E8] px-6 text-sm font-bold text-white hover:opacity-90"
                >
                  + Create Experience
                </Button>
              </div>
            )}
      </div>
      <AddExperienceDialog
        open={openAddExperience}
        onClose={() => setOpenAddExperience(false)}
        tenantName={tenantName}
        campaignId={campaignId}
        deepLink={deepLink}
        defaultCampaign={campaignName}
      />
      <ExperienceUsersListDialog
        open={openUsersDialog}
        onClose={() => {
          setOpenUsersDialog(false);
          setSelectedExperience(null);
        }}
        experienceDetails={selectedExperience}
      />
    </div>
  );
};

const ExperienceCard = ({
  experience,
  onOpenConfigurator,
  onEditDetails,
  onSeeUsers,
  onRemove,
}) => {
  return (
    <div className="rounded-lg border border-brand-17 bg-brand-100 px-6 py-6 shadow-lg">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[140px_1fr_1px_210px] lg:items-center">
        <div
          className="flex h-32 w-32 items-center justify-center rounded-xl"
          style={{ backgroundColor: experience.cardColor || "#00552D" }}
        >
          {/* <Box className="h-16 w-16 text-neutral-100/45" /> */}
          <img
            src={rotation3dWhiteIcon}
            className="h-16 w-16 text-neutral-100/45"
          />
        </div>

        <div>
          <h2 className="text-2xl font-bold text-neutral-100">
            {/* {experience.title} */}
            {experience.name}
          </h2>

          <p className="mt-1 text-sm text-neutral-100/60">
            Created {formatDate(experience.createdAt)}
          </p>

          <p className="mt-6 max-w-3xl text-sm leading-relaxed text-neutral-100/85">
            {experience.description}
          </p>

          <div className="mt-7 flex flex-wrap gap-3">
            <StatusBadge status={experience.status}>
              Status: {experience.status}
            </StatusBadge>

            <span className="rounded-md bg-[#6B6100] px-3 py-2 text-xs font-bold text-yellow-300">
              Difficulty: {experience.difficulty || "-"}
            </span>

            <span className="rounded-md bg-[#004C75] px-3 py-2 text-xs font-bold text-[#00D3F2]">
              Approx Duration: {formatDuration(experience.estimate_time) || "-"}
            </span>
          </div>
        </div>

        <div className="hidden h-full w-px bg-[#695CA8] lg:block" />

        <div className="flex flex-wrap gap-3 sm:items-end">
          <ActionButton
            // disabled={experience.disabledConfigurator}
            onClick={onOpenConfigurator}
            icon={<SlidersHorizontal className="h-4 w-4" />}
          >
            Edit Experience
          </ActionButton>

          {/* <ActionButton
            onClick={onEditDetails}
            icon={<PencilLine className="h-4 w-4" />}
          >
            Edit Details
          </ActionButton> */}

          <ActionButton
            onClick={onSeeUsers}
            icon={<Users className="h-4 w-4" />}
          >
            See all Users
          </ActionButton>

          {/* <ActionButton
            danger
            onClick={onRemove}
            icon={<Trash2 className="h-4 w-4" />}
          >
            Remove Experience
          </ActionButton> */}
        </div>
      </div>
    </div>
  );
};

const StatusBadge = ({ status, children }) => {
  const isPublished = status === "PUBLISHED";

  return (
    <span
      className={`rounded-md px-3 py-2 text-xs font-bold ${
        isPublished
          ? "bg-[#064D23] text-[#00E261]"
          : "bg-[#064D23] text-[#00E261]"
      }`}
    >
      {children}
    </span>
  );
};

const ActionButton = ({ children, icon, onClick, disabled, danger }) => {
  return (
    <Button
      type="button"
      variant="outline"
      disabled={disabled}
      onClick={onClick}
      className={`h-8 w-full justify-start border-[#00A9D6] bg-transparent px-4 text-xs font-bold hover:bg-white/5 sm:w-[170px] ${
        danger
          ? "text-[#FF4D5D] hover:text-[#FF4D5D]"
          : "text-neutral-100 hover:text-neutral-100"
      } ${disabled ? "cursor-not-allowed opacity-40" : ""}`}
    >
      <span className="mr-2">{icon}</span>
      {children}
    </Button>
  );
};

export default ExperienceListing;
